import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, RefreshCw, Zap, Phone, ChevronUp, ChevronDown,
  ArrowUpDown, AlertTriangle, Inbox, Plus, MessageCircle,
} from 'lucide-react';
import i18n from '../i18n';
import { getRichieste, countRichieste, type RichiestaListItem } from '../api/richieste';
import Badge from '../components/ui/Badge';
import NotaInternaRiga from '../components/richieste/NotaInternaRiga';
import StatusBadge from '../components/ui/StatusBadge';
import { SkeletonRow } from '../components/ui/Skeleton';
import { useAuthStore } from '../store/authStore';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel, categoriaVariant } from '../lib/categorie';
import { RICHIESTE_WRITE_ROLES } from '../lib/constants';

// ── Costanti tab ──────────────────────────────────────────────────────────────

// Tab fase Richiesta — allineato a order_status_v2 (#95).
// "in_consultazione" è uno stato laterale/bloccante (#96) della fase Richiesta:
// può comparire da uno qualsiasi degli stati pre-conferma. Lo mostriamo come tab
// dedicata per evidenziare gli ordini che richiedono parere RT/DIR.
// `label` = chiave i18n (tradotta al render). Testo tab verbatim dal vecchio
// catalogo (es. "Preventivo+omologa generati", senza spazi attorno al +).
const STATUS_TABS = [
  { status: 'ricevuto', label: 'richieste.tab.ricevuto' },
  { status: 'preventivo_omologa_generati', label: 'richieste.tab.preventivo_omologa_generati' },
  { status: 'inviato_cliente', label: 'richieste.tab.inviato_cliente' },
  { status: 'confermato', label: 'richieste.tab.confermato' },
  { status: 'in_consultazione', label: 'richieste.tab.in_consultazione' },
  { status: 'annullato', label: 'richieste.tab.annullato' },
] as const;

type TabStatus = (typeof STATUS_TABS)[number]['status'];

// ── Helpers ─────────────────────────────────────────────────────────────────

function relativeDate(days: number): string {
  if (days === 0) return i18n.t('richieste.lista.oggi');
  if (days === 1) return i18n.t('richieste.lista.ieri');
  return i18n.t('richieste.lista.giorni_fa', { giorni: days });
}

function formatDateIT(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

type SortDir = 'asc' | 'desc';
type SortKey = 'codice_ordine' | 'customer' | 'categoria' | 'days_pending' | 'created_at';

// `label` = chiave i18n (tradotta al render).
function modalitaBadge(m: string): { label: string; variant: 'info' | 'purple' | 'neutral' } {
  if (m === 'porto') return { label: 'richieste.modalita.porto', variant: 'info' };
  if (m === 'ritiro') return { label: 'richieste.modalita.ritiro', variant: 'purple' };
  return { label: 'richieste.modalita.nd', variant: 'neutral' };
}

function daysPendingColor(days: number): string {
  if (days > 7) return 'text-accent-danger font-semibold';
  if (days > 3) return 'text-accent-warning font-semibold';
  return 'text-ink-mid';
}

// Risolto a import-time (pagina lazy, i18n già inizializzato in main).
const PERIODO_OPTIONS = [
  { value: '', label: i18n.t('richieste.filtri.periodo_qualsiasi') },
  { value: '7', label: i18n.t('richieste.filtri.periodo_7') },
  { value: '30', label: i18n.t('richieste.filtri.periodo_30') },
  { value: '90', label: i18n.t('richieste.filtri.periodo_90') },
];

// ── Componenti interni ───────────────────────────────────────────────────────

function SortIcon({ col, sortBy, sortDir }: { col: string; sortBy: SortKey; sortDir: SortDir }) {
  if (sortBy !== col) return <ArrowUpDown size={13} className="text-ink-faint" />;
  return sortDir === 'asc'
    ? <ChevronUp size={13} className="text-sage" />
    : <ChevronDown size={13} className="text-sage" />;
}

function FilterSelect({
  value, onChange, options, testId,
}: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; testId?: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="px-3 py-2 text-sm border border-stone-border rounded-md bg-white text-ink-dark focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ── Componente principale ────────────────────────────────────────────────────

export default function RichiestePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  // Stesso set della rotta che il bottone apre (`/richieste/nuova`). Prima
  // includeva AMM: ramo morto — AMM non arriva qui, ne' dalla Sidebar ne' da
  // RICHIESTE_VIEW_ROLES — ma contraddiceva le due rotte che lo racchiudono.
  // NB: il backend non arbitra questo caso: POST /richieste e' l'endpoint
  // pubblico della landing e non ha require_role. Qui il FE e' l'unica sede.
  const canCreate = (RICHIESTE_WRITE_ROLES as string[]).includes(user?.role ?? '');

  const activeStatus = (searchParams.get('status') ?? 'ricevuto') as TabStatus;

  const [richieste, setRichieste] = useState<RichiestaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});

  const { categorie: categorieList } = useCategorie();
  const categoriaOptions = useMemo(
    () => [
      { value: '', label: t('richieste.filtri.tutte_categorie') },
      ...categorieList.map((c) => ({ value: c.codice, label: c.nome_visualizzato })),
    ],
    [categorieList, t],
  );

  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [periodo, setPeriodo] = useState('');
  // #107g: sotto-filtro per tipo consultazione (solo tab in_consultazione).
  // Inizializzato dal query param (deep-link dalla coda di lavoro in dashboard).
  const [consultazioneTipo, setConsultazioneTipo] = useState<'' | 'RT' | 'DIR'>(() => {
    const t = searchParams.get('consultazione_tipo');
    return t === 'RT' || t === 'DIR' ? t : '';
  });
  const [sortBy, setSortBy] = useState<SortKey>('days_pending');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchRichieste = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const filters = {
          status: activeStatus,
          ...(categoria && { categoria }),
          ...(periodo && { days_from: parseInt(periodo, 10) }),
          ...(activeStatus === 'in_consultazione' && consultazioneTipo
            ? { consultazione_tipo: consultazioneTipo }
            : {}),
        };
        const data = await getRichieste(filters);
        setRichieste(data);
      } catch {
        setError(t('richieste.lista.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [activeStatus, categoria, periodo, consultazioneTipo, t]);

  const fetchCounts = useCallback(() => {
    void (async () => {
      const counts = await Promise.allSettled(
        STATUS_TABS.map((t) => countRichieste(t.status)),
      );
      const map: Record<string, number> = {};
      STATUS_TABS.forEach((t, i) => {
        const r = counts[i];
        map[t.status] = r.status === 'fulfilled' ? r.value : 0;
      });
      setTabCounts(map);
    })();
  }, []);

  useEffect(() => {
    fetchRichieste();
  }, [fetchRichieste]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const filtered = useMemo(() => {
    let list = [...richieste];
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.codice_ordine.toLowerCase().includes(s) ||
          r.customer.ragione_sociale.toLowerCase().includes(s) ||
          (r.referente_richiesta ?? '').toLowerCase().includes(s),
      );
    }
    list.sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      switch (sortBy) {
        case 'customer': av = a.customer.ragione_sociale; bv = b.customer.ragione_sociale; break;
        case 'days_pending': av = a.days_pending; bv = b.days_pending; break;
        case 'created_at': av = a.created_at; bv = b.created_at; break;
        default: av = a.codice_ordine; bv = b.codice_ordine;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av); const bs = String(bv);
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return list;
  }, [richieste, search, sortBy, sortDir]);

  const handleSort = (col: SortKey) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const handleTabChange = (s: TabStatus) => {
    setSearchParams({ status: s });
    setSearch('');
    setCategoria('');
    setPeriodo('');
    setConsultazioneTipo('');
  };

  const dangerCount = richieste.filter((r) => r.days_pending > 7).length;
  const warningCount = richieste.filter((r) => r.days_pending > 3 && r.days_pending <= 7).length;

  const activeTabLabel = STATUS_TABS.find((t) => t.status === activeStatus)?.label ?? activeStatus;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-dark tracking-tight mb-1">
            {t('common.nav.richieste')}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-ink-soft">
              <strong className="text-ink-dark font-semibold">{richieste.length}</strong>{' '}
              {t(activeTabLabel).toLowerCase()}
            </span>
            {dangerCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-accent-danger-bg text-accent-danger border-[#FECACA]">
                <AlertTriangle size={11} />
                {t('richieste.lista.oltre_7_giorni', { count: dangerCount })}
              </span>
            )}
            {warningCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-accent-warning-bg text-accent-warning border-[#FDE68A]">
                <AlertTriangle size={11} />
                {t('richieste.lista.tra_4_7_giorni', { count: warningCount })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <button
              type="button"
              data-testid="nuova-richiesta-button"
              onClick={() => navigate('/richieste/nuova')}
              className="flex items-center gap-2 px-3.5 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-[#5C6F59] transition-colors"
            >
              <Plus size={14} />
              <span>{t('richieste.lista.nuova_richiesta')}</span>
            </button>
          )}
          <button
            type="button"
            data-testid="richieste-refresh-button"
            onClick={() => { fetchRichieste(); fetchCounts(); }}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border border-stone-border rounded-lg text-sm text-ink-mid hover:bg-stone-bg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>{t('common.azioni.aggiorna')}</span>
          </button>
        </div>
      </div>

      {/* Tab stati */}
      <div className="flex gap-0.5 overflow-x-auto mb-4 border-b border-stone-mid">
        {STATUS_TABS.map((tab) => {
          const isActive = tab.status === activeStatus;
          const count = tabCounts[tab.status];
          return (
            <button
              key={tab.status}
              type="button"
              data-testid={`tab-${tab.status}`}
              onClick={() => handleTabChange(tab.status)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                isActive
                  ? 'border-sage text-sage bg-sage-light/30'
                  : 'border-transparent text-ink-mid hover:text-ink-dark hover:border-stone-border'
              }`}
            >
              <StatusBadge status={tab.status} size="sm" />
              {count != null && count > 0 && (
                <span className={`min-w-[20px] h-5 px-1 text-[11px] font-semibold rounded-full flex items-center justify-center ${
                  isActive ? 'bg-sage text-white' : 'bg-stone-mid text-ink-mid'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar filtri */}
      <div className="bg-white border border-stone-mid rounded-xl px-4 py-3 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <input
            type="text"
            data-testid="richieste-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('richieste.filtri.cerca_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage text-ink-dark"
          />
        </div>
        <FilterSelect value={categoria} onChange={setCategoria} options={categoriaOptions} testId="filtro-categoria" />
        <FilterSelect value={periodo} onChange={setPeriodo} options={PERIODO_OPTIONS} testId="filtro-periodo" />
        {activeStatus === 'in_consultazione' && (
          <FilterSelect
            value={consultazioneTipo}
            onChange={(v) => setConsultazioneTipo(v as '' | 'RT' | 'DIR')}
            options={[
              { value: '', label: t('richieste.filtri.tutte_consultazioni') },
              { value: 'RT', label: t('richieste.filtri.verso_rt') },
              { value: 'DIR', label: t('richieste.filtri.verso_dir') },
            ]}
            testId="filtro-consultazione-tipo"
          />
        )}
        {(search || categoria || periodo || consultazioneTipo) && (
          <button
            type="button"
            onClick={() => { setSearch(''); setCategoria(''); setPeriodo(''); setConsultazioneTipo(''); }}
            className="text-sm text-ink-soft hover:text-ink-dark transition-colors"
          >
            {t('richieste.filtri.pulisci')}
          </button>
        )}
      </div>

      {/* Contenuto */}
      {error ? (
        <div className="bg-accent-danger-bg border border-[#FECACA] rounded-xl p-6 text-center">
          <p className="text-accent-danger font-medium mb-3">{error}</p>
          <button type="button" onClick={fetchRichieste} className="text-sm text-sage underline">
            {t('common.azioni.riprova')}
          </button>
        </div>
      ) : filtered.length === 0 && !loading ? (
        <div className="bg-white border border-stone-mid rounded-xl p-12 text-center">
          <Inbox size={40} className="text-ink-faint mx-auto mb-3" />
          <p className="text-ink-mid font-medium">{t('richieste.lista.vuota', { stato: t(activeTabLabel).toLowerCase() })}</p>
          {(search || categoria || periodo) && (
            <p className="text-ink-soft text-sm mt-1">{t('richieste.lista.modifica_filtri')}</p>
          )}
        </div>
      ) : (
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-mid bg-stone-bg">
                {([
                  { key: 'codice_ordine', label: t('richieste.col.codice') },
                  { key: 'customer', label: t('richieste.col.cliente') },
                  { key: 'categoria', label: t('richieste.col.categoria') },
                  { key: null, label: t('richieste.col.ce') },
                  { key: null, label: t('richieste.col.modalita') },
                  { key: 'days_pending', label: t('richieste.col.attesa') },
                  { key: null, label: t('richieste.col.referente') },
                  { key: null, label: '' },
                ] as { key: SortKey | null; label: string }[]).map(({ key, label }, i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide ${key ? 'cursor-pointer hover:text-ink-dark select-none' : ''}`}
                    onClick={key ? () => handleSort(key) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {key && <SortIcon col={key} sortBy={sortBy} sortDir={sortDir} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)
                : filtered.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`richiesta-row-${r.id}`}
                      className="border-b border-stone-mid last:border-0 hover:bg-stone-bg transition-colors cursor-pointer"
                      onClick={() => navigate(`/richieste/${r.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-sage">
                        {r.codice_ordine}
                      </td>
                      <td className="px-4 py-3 text-ink-dark font-medium max-w-[260px]">
                        <span className="block truncate">{r.customer.ragione_sociale}</span>
                        <NotaInternaRiga nota={r.nota_interna} />
                        {r.consultazione_tipo && (
                          <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-[#F5F3FF] text-[#7C3AED] border border-[#DDD6FE]">
                            <MessageCircle size={10} /> {t('richieste.lista.consultazione_verso', { tipo: r.consultazione_tipo })}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={categoriaVariant(r.categoria)}>
                          {categoriaLabel(r.categoria, categorieList)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {r.conto_energia === 'si'
                          ? <Zap size={15} className="text-accent-warning" />
                          : <span className="text-ink-faint">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={modalitaBadge(r.modalita_conferimento).variant}>
                          {t(modalitaBadge(r.modalita_conferimento).label)}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 ${daysPendingColor(r.days_pending)}`}>
                        <div>{relativeDate(r.days_pending)}</div>
                        <div className="text-[11px] text-ink-faint font-normal">
                          {formatDateIT(r.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-dark">{r.referente_richiesta}</div>
                        <a
                          href={`tel:${r.telefono_richiesta}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[11px] text-sage hover:underline flex items-center gap-1"
                        >
                          <Phone size={11} />
                          {r.telefono_richiesta}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          data-testid={`apri-richiesta-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/richieste/${r.id}`); }}
                          className="px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-md hover:bg-sage-light transition-colors"
                        >
                          {t('richieste.lista.apri')}
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
          {!loading && filtered.length > 0 && (
            <div className="px-4 py-2.5 border-t border-stone-mid bg-stone-bg text-xs text-ink-soft">
              {filtered.length !== richieste.length
                ? t('richieste.lista.conteggio_filtrato', { mostrate: filtered.length, totale: richieste.length })
                : t('richieste.lista.conteggio', { count: filtered.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
